import { useEffect, useState } from 'react';

const breakpoints = {
  base: 639,
  sm: 640,
  md: 960,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

type Breakpoint = keyof typeof breakpoints;

const orderedBreakpoints = ['base', ...Object.keys(breakpoints)];

function getMediaQuery(bp: Breakpoint) {
  const min = breakpoints[bp];
  return `(min-width: ${min}px)`;
}

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<string>('base');

  useEffect(() => {
    const entries = orderedBreakpoints.map((bp) => ({
      bp,
      mql: window.matchMedia(getMediaQuery(bp as Breakpoint))
    }));

    const getCurrent = () => {
      for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].mql.matches) return entries[i].bp;
      }
      return 'base';
    };

    const update = () => setBreakpoint(getCurrent());

    entries.forEach(({ mql }) => mql.addEventListener('change', update));
    update();

    return () =>
      entries.forEach(({ mql }) => mql.removeEventListener('change', update));
  }, []);

  const isBreakpoint = (min: string, max?: string) => {
    const currentIndex = orderedBreakpoints.indexOf(breakpoint);
    const minIndex = orderedBreakpoints.indexOf(min);
    const maxIndex = max
      ? orderedBreakpoints.indexOf(max)
      : Number.POSITIVE_INFINITY;
    return currentIndex >= minIndex && currentIndex <= maxIndex;
  };

  return { breakpoint, isBreakpoint };
}
